import { getCurrentTenant, registerTenantReset } from './tenantScope';

/**
 * settingsListCache — the single home for the useSettingsList result cache.
 *
 * ── Why this module exists (dispatch 413) ──────────────────────────────
 *
 * Before this module, the cache lived inside `hooks/useSettingsList.js`.
 * Consequence: `services/api.js` (the writer surface —
 * settingsAPI.addListItem / updateListItem / deleteListItem / reorderList)
 * could not invalidate the cache without importing from `hooks/`, which
 * would have created a cycle (hooks already import from services). Each
 * writer therefore silently left the cache stale, and downstream
 * consumers reading a listType after a write saw the pre-write data
 * until manual refetch. The WO create sheet → InlineCreate → resource
 * chooser was the surface where this became a Family-A defect visible
 * to users.
 *
 * By moving the cache to a neutral leaf module, both:
 *   - `services/api.js` (the writer) can invalidate on every successful
 *     mutation
 *   - `hooks/useSettingsList.js` (the reader) can populate/read
 * without either importing from the other. The cache is now invalidated
 * AT THE WRITER, per the "fix at the primitive, not call sites" rule.
 *
 * ── Mutators that DO NOT invalidate this cache (dispatch 414 §2) ──────
 *
 * Not every method on `settingsAPI` is a Settings-list writer. These
 * bypass invalidation deliberately:
 *
 *   - updateTaxRates            (single-row config, not a list)
 *   - createQuoteTemplate,      updateQuoteTemplate,  deleteQuoteTemplate
 *   - createTCTemplate,         updateTCTemplate,     deleteTCTemplate
 *
 * These do not use the `list_type` axis and no current `useSettingsList`
 * consumer reads `tax_rates` or `tc_templates` (verified: only
 * customer_types, crews, and resource_types are read through this
 * cache today). The safety is therefore CONDITIONAL on that consumer
 * set. If a future edit adds `useSettingsList('tax_rates')` (or
 * `useSettingsList('tc_templates')`), the corresponding mutators
 * become silent staleness sources — wrap them the same way
 * addListItem is wrapped in services/api.js.
 *
 * ── Named already-mounted consumers (dispatch 414 §3) ─────────────────
 *
 * The reactive-layer limit below is not abstract. As of PR-72 Commit 3:
 *
 *   JobDetail (:470, :474) reads `crews` and `resource_types` via
 *   useSettingsList, then passes them DOWN AS PROPS to WOCreateSheets
 *   (:915-916). The sheet holds them in local state seeded from those
 *   props (WOCreateSheets:108-109) with sync effects at :135-137.
 *
 *   After an inline create from the sheet: the cache invalidates, but
 *   JobDetail is already mounted and does NOT refetch its
 *   `useSettingsList('resource_types')` result. The sheet's
 *   `onCreated` handler (WOCreateSheets:655) locally appends the new
 *   entity, and the sync effect at :136 does not fire because the
 *   prop identity does not change. Both mechanisms are load-bearing
 *   together.
 *
 *   Fragility: any future edit that causes `resourceList`'s identity
 *   to update (e.g. wrapping in useMemo, or JobDetail calling refetch)
 *   without keeping the local append in sync will fire
 *   setResourceOptions(resources) at :136 and OVERWRITE the local
 *   append with the stale (pre-invalidation) list. The user silently
 *   loses the resource they just created.
 *
 *   Asymmetry to notice: `useResourceCategories()` (categories
 *   dropdown in the same sheet) mounts fresh INSIDE the sheet, so it
 *   sees the invalidated cache and stays correct. Resources come DOWN
 *   AS A PROP from a component mounted long before. Same cache, same
 *   write, opposite freshness — decided entirely by where the
 *   consumer sits.
 *
 * ── Reactive layer (PR-74 · dispatch 443 · IDI-83) ────────────────────
 *
 * The pub/sub layer named as "out of PR-72 scope" (dispatch 413 §4
 * addendum, above) was added in PR-74 to close IDI-83.
 *
 * A cache between a writer and a reader is itself a source of truth
 * (§6.18). Invalidating storage without notifying consumers means the
 * write is visible ONLY to components that mount after it — but
 * SettingsProvider wraps the whole router and mounts once per page
 * load, so ~12 downstream consumers (getEnumLabel/getOptions across
 * customers/quotes/invoices/change-orders/jobs/work-orders) rendered
 * stale labels between a settings save and the next full reload.
 *
 * subscribe() lets a mounted consumer be notified when a listType's
 * cache entry changes. invalidate() gained the emit; every existing
 * writer that already invalidates therefore also notifies for free —
 * per §3's "single signal, two consumers" ruling.
 *
 * Listener isolation is load-bearing, not defensive padding
 * (dispatch 443 §1): a settings save must not fail because a reader
 * threw. One throwing listener does not stop the others being
 * notified, and invalidate() never propagates a listener's throw
 * back into the writer. Assert this.
 *
 * Public API:
 *   getCached(listType) → { items } | undefined
 *   setCached(listType, { items }) → void
 *   invalidate(listType?) → void   // one listType, or all; notifies subscribers
 *   hasCached(listType) → boolean
 *   subscribe(listener) → unsubscribe  // listener: (listType) => void
 *                                         listType undefined = "all"
 */

// IDI-304: entries are stamped with the tenant they were filled for and are
// never served to a different tenant; the whole map is also dropped on
// logout / tenant change (tenantScope). Without this, dealer B saw dealer A's
// crews, job types, lead sources ... after a same-tab logout -> login.
const cache = new Map(); // listType -> { tenant, entry }

// IDI-714: the signal key for "Quote Defaults changed" (settingsAPI's two
// Quote Defaults writers send it). Not a list type: SettingsProvider re-reads
// only Quote Defaults on it, never a list.
export const QUOTE_DEFAULTS_SIGNAL = 'quote_defaults';
// IDI-718: Company Profile and Branding saves. Not list types either: only
// useCompanyProfile / useBranding re-read on them (their own copy, one read).
export const COMPANY_PROFILE_SIGNAL = 'company_profile';
export const BRANDING_SIGNAL = 'branding';

export function getCached(listType) {
  const hit = cache.get(listType);
  if (!hit) return undefined;
  if (hit.tenant !== getCurrentTenant()) {
    cache.delete(listType);
    return undefined;
  }
  return hit.entry;
}

export function setCached(listType, entry) {
  if (!listType || !entry) return;
  cache.set(listType, { tenant: getCurrentTenant(), entry });
}

// Subscribers to invalidate() notifications. Set semantics: idempotent
// on repeat-subscribe, cheap iteration for the ~O(1) subscriber count
// SettingsProvider will realistically add.
const listeners = new Set();

export function invalidate(listType) {
  if (listType) cache.delete(listType);
  else cache.clear();
  // Notify subscribers. Per dispatch 443 §1: one throwing listener must
  // NOT stop the others being notified, and must NOT propagate the
  // throw back into the writer (a settings save that fails because a
  // reader threw would be a worse defect than the one we are fixing).
  //
  // The try/catch inside forEach is load-bearing, not defensive padding.
  // Tested by T-listener-throw-isolated in settingsListCache.test.js.
  listeners.forEach((fn) => {
    try {
      fn(listType);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('settingsListCache subscriber threw:', err);
    }
  });
}

/**
 * subscribe(listener) — register a callback fired on every invalidate().
 *
 * listener signature: (listType: string | undefined) => void
 *   listType is the argument passed to invalidate(); undefined means
 *   "all lists invalidated".
 *
 * Returns an unsubscribe function. Idempotent — calling unsubscribe
 * twice is safe. Idempotent on repeat-subscribe of the same function
 * (Set semantics): calling subscribe(fn) twice with the same fn
 * registers it once, and one unsubscribe removes it.
 *
 * The primary consumer is SettingsProvider (hooks/useSettings.js),
 * which subscribes on mount and unsubscribes on unmount, calling
 * refresh(listType) from the listener so the visible label re-fetches
 * without a page reload.
 */
export function subscribe(listener) {
  if (typeof listener !== 'function') {
    throw new TypeError('settingsListCache.subscribe: listener must be a function');
  }
  listeners.add(listener);
  return function unsubscribe() {
    listeners.delete(listener);
  };
}

// IDI-507 (4): drop ONE entry SILENTLY (no subscriber notify). For a large
// list only one page reads at a time (the pricebook): the next reader just
// refetches. A notify would make every SettingsProvider refetch 4,259 rows on
// each catalog save, and notify-driven fetches caused the 2026-09-29 storm.
export function drop(listType) {
  if (listType) cache.delete(listType);
}

export function hasCached(listType) {
  return getCached(listType) !== undefined;
}

// Logout / tenant switch: drop every entry SILENTLY (no subscriber notify).
// Notifying would make SettingsProvider refetch every list immediately; right
// after logout there is no session, each refetch 401s, the 401 dispatches
// auth:session-expired -> logout() -> reset -> notify -> refetch ... an
// unbounded request storm (observed live on 58e8e45: 13,892 requests per
// list). SettingsProvider is keyed on the signed-in identity and remounts
// for the next user, so it reloads on its own; getCached's tenant check
// guarantees nothing stale is served in between.
registerTenantReset(() => cache.clear());

// Test-only: expose the underlying map for inspection. Do not use in
// production code — read/write via the accessors above.
export function __getInternalCacheForTests() {
  return cache;
}

// Test-only: clear the subscriber set between tests. Jest module-scope
// state persists across test() calls in the same file; without this,
// a subscriber registered in test A fires (or is asserted-called) in
// test B and produces spurious cross-test coupling. Called from each
// test's afterEach / beforeEach as needed.
export function __resetListenersForTests() {
  listeners.clear();
}
